/**
 * One collator for the whole product.
 *
 * Sorting a Spanish name list with the default comparison puts `Álvarez` after `Zorrilla` and `ñ`
 * after `z`, because JavaScript's `<` compares UTF-16 code units. The fix is a locale-aware
 * collator, and the requirement is that there be exactly ONE of them: two collators built in two
 * places with two option sets produce two orderings, and a paginated list ordered by one and
 * cursored by the other silently skips and repeats rows.
 *
 * `sensitivity: 'base'` means `Álvarez`, `Alvarez` and `ÁLVAREZ` all compare equal, which is what
 * ranking and duplicate detection want. It is also why {@link compareForSort} exists: a comparator
 * that returns 0 for two different strings gives an unstable order, and an unstable order is a
 * pagination defect. That function keeps the collator as the primary key and breaks ties by code
 * point, so the result is locale-correct AND total.
 */
/** The single shared collator: Spanish, accent- and case-insensitive. */
export const COLLATOR = new Intl.Collator('es', { sensitivity: 'base' });
/**
 * Locale-aware comparison at base sensitivity: 0 means "the same word, written differently".
 *
 * Use it for ranking and for equality. Do not use it to sort a list that will be paginated; use
 * {@link compareForSort}.
 */
export function compare(left, right) {
    return COLLATOR.compare(left, right);
}
/** Whether two strings are the same word ignoring case and accents. */
export function equalsLoosely(left, right) {
    return COLLATOR.compare(left, right) === 0;
}
/**
 * Code-point order: the tie-break, and the comparator for anything that is not prose.
 *
 * Identifiers, codes, keys and file names are sorted with this rather than with the collator. It
 * is defined for every pair of strings, it returns 0 only for equal strings, and it never changes
 * with an ICU version or with the machine's locale, which is what a stored pagination cursor and a
 * reproducible test both need.
 */
export function compareCodePoints(left, right) {
    if (left < right) {
        return -1;
    }
    return left > right ? 1 : 0;
}
/**
 * A total order over prose: the shared collator first, then the code-point tie-break.
 *
 * The tie-break is deliberately code-point rather than a second collator at a higher sensitivity,
 * for the reasons above: a comparator that returns 0 for two different strings gives an unstable
 * order, and an unstable order paginated by cursor skips and repeats rows.
 */
export function compareForSort(left, right) {
    const collated = COLLATOR.compare(left, right);
    return collated === 0 ? compareCodePoints(left, right) : collated;
}
//# sourceMappingURL=collator.js.map